import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { BirthdaysCard } from "@/components/BirthdaysCard";
import { StreakCard, TopStreaksPreview } from "@/components/StreakCard";
import { NoticesCard } from "@/components/NoticesCard";
import { Receipt, ChatCircleDots, Wallet, ClockClockwise, ArrowRight, Plus } from "@phosphor-icons/react";

function StatCard({ label, value, icon: Icon, accent }) {
    return (
        <Card className="border-[#E2E8F0] an-card-hover" data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            <CardContent className="p-5">
                <div className="flex items-start justify-between">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1.5">
                            {label}
                        </div>
                        <div className="text-3xl font-bold text-[#003366]" style={{ fontFamily: "Outfit" }}>
                            {value}
                        </div>
                    </div>
                    <div className={`h-10 w-10 rounded-md flex items-center justify-center ${accent}`}>
                        <Icon size={20} weight="bold" className="text-white" />
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

export default function TechDashboard() {
    const { user } = useAuth();
    const [reports, setReports] = useState([]);
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        Promise.all([api.get("/reports"), api.get("/requests")])
            .then(([rRes, qRes]) => {
                if (cancelled) return;
                setReports(Array.isArray(rRes?.data) ? rRes.data : []);
                setRequests(Array.isArray(qRes?.data) ? qRes.data : []);
            })
            .catch(() => {
                if (cancelled) return;
                setReports([]);
                setRequests([]);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, []);

    const latest = reports[0];
    const totalEarned = reports.reduce((sum, r) => sum + (r.total_pay || 0), 0);
    const pendingReqs = requests.filter((r) => r.status === "pending" || r.status === "in_review").length;

    return (
        <div className="space-y-8">
            <div>
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1.5">Dashboard</div>
                <h1 className="text-3xl sm:text-4xl font-bold text-[#003366]" style={{ fontFamily: "Outfit" }}>
                    Welcome back, {user?.full_name?.split(" ")[0] || "Technician"}
                </h1>
                <p className="text-slate-600 mt-2 text-sm">Here's a quick overview of your account activity.</p>
            </div>

            {loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[1, 2, 3].map((i) => (
                        <Skeleton key={i} className="h-28 rounded-md" />
                    ))}
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <StatCard label="Total Reports" value={reports.length} icon={Receipt} accent="bg-[#0066D9]" />
                    <StatCard
                        label="Total Earned"
                        value={`$${totalEarned.toLocaleString("en-US", { maximumFractionDigits: 2 })}`}
                        icon={Wallet}
                        accent="bg-[#003366]"
                    />
                    <StatCard label="Open Requests" value={pendingReqs} icon={ClockClockwise} accent="bg-[#E6002C]" />
                </div>
            )}

            {/* Work streak */}
            <StreakCard />

            {/* Notices from the office */}
            <NoticesCard />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {/* Latest Report */}
                <Card className="border-[#E2E8F0]">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-5">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">
                                    Latest report
                                </div>
                                <h3 className="text-lg font-semibold text-[#1F2A44]" style={{ fontFamily: "Outfit" }}>
                                    Most recent payment
                                </h3>
                            </div>
                            <Receipt size={22} className="text-[#0066D9]" weight="duotone" />
                        </div>
                        {latest ? (
                            <div className="space-y-3">
                                <div className="text-sm text-slate-500">Week period</div>
                                <div className="text-base font-medium text-[#003366]">
                                    {latest.week_start} → {latest.week_end}
                                </div>
                                <div className="flex items-baseline gap-2 pt-2">
                                    <div className="text-3xl font-bold text-[#003366]" style={{ fontFamily: "Outfit" }}>
                                        ${Number(latest.total_pay || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}
                                    </div>
                                    <div className="text-xs text-slate-500">total pay</div>
                                </div>
                                <Link to="/reports" data-testid="dashboard-view-reports-link">
                                    <Button variant="outline" className="mt-3 border-[#003366] text-[#003366] hover:bg-slate-50">
                                        View all reports <ArrowRight size={16} className="ml-1" />
                                    </Button>
                                </Link>
                            </div>
                        ) : (
                            <div className="text-sm text-slate-500 py-6">
                                No payment reports yet. They'll appear here when your admin creates one.
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Quick action - Submit Request */}
                <Card className="border-[#E2E8F0] bg-[#003366] text-white">
                    <CardContent className="p-6 flex flex-col h-full">
                        <div className="flex items-center justify-between mb-5">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.2em] text-white/70 font-semibold">
                                    Quick action
                                </div>
                                <h3 className="text-lg font-semibold" style={{ fontFamily: "Outfit" }}>
                                    Need to report something?
                                </h3>
                            </div>
                            <ChatCircleDots size={22} weight="duotone" className="text-[#0066D9]" />
                        </div>
                        <p className="text-sm text-white/80 leading-relaxed flex-1">
                            Submit payment questions, missing hours corrections, equipment requests, or general
                            issues directly to your admin team.
                        </p>
                        <Link to="/requests" className="mt-5" data-testid="dashboard-new-request-link">
                            <Button className="bg-[#0066D9] hover:bg-[#0052ad] text-white">
                                <Plus size={16} className="mr-1.5" /> Submit a request
                            </Button>
                        </Link>
                    </CardContent>
                </Card>
            </div>

            {/* Birthdays (read-only) + Top work streaks */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <BirthdaysCard />
                <TopStreaksPreview />
            </div>

            {/* Recent requests */}
            <Card className="border-[#E2E8F0]">
                <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-5">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">
                                Activity
                            </div>
                            <h3 className="text-lg font-semibold text-[#1F2A44]" style={{ fontFamily: "Outfit" }}>
                                Recent requests
                            </h3>
                        </div>
                    </div>
                    {requests.length === 0 ? (
                        <div className="text-sm text-slate-500 py-6">You haven't submitted any requests yet.</div>
                    ) : (
                        <div className="divide-y divide-[#E2E8F0]">
                            {requests.slice(0, 5).map((r) => (
                                <div
                                    key={r.id}
                                    className="flex items-center justify-between py-3"
                                    data-testid={`recent-request-${r.id}`}
                                >
                                    <div>
                                        <div className="text-sm font-medium text-[#1F2A44]">{r.subject}</div>
                                        <div className="text-xs text-slate-500 mt-0.5 capitalize">
                                            {r.type.replace(/_/g, " ")} · {new Date(r.created_at).toLocaleDateString()}
                                        </div>
                                    </div>
                                    <StatusBadge status={r.status} />
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
